import React, { useEffect, useState, useCallback, useRef } from 'react';
import { apiFetch } from '../../../../lib/api';
import { formatCurrency } from '../../../../utils/formatters';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoOrdine = 'BOZZA' | 'CONFERMATO' | 'IN_LAVORAZIONE' | 'CHIUSO';
type ModalitaEvasione = 'SAL' | 'DDT' | 'RAPPORTINO' | 'MISTA';
type DestinatarioOrdine = 'IMPRESA' | 'ACQUIRENTE';

interface OrdineListaItem {
  id: number;
  numero: string;
  societaId: number;
  commessaId: number;
  dataOrdine: string;
  stato: StatoOrdine;
  modalitaEvasione: ModalitaEvasione | null;
  destinatarioOrdine: DestinatarioOrdine | null;
  acquirenteId: number | null;
  totale: string | null;
  preventivoOrigineId: number | null;
  commessa: { id: number; numero: string; descrizione: string | null; clienteRagioneSociale: string | null } | null;
  acquirente: { id: number; nome: string | null; cognome: string | null; unitaCodice: string | null } | null;
}

interface AcquirenteOption {
  id: number;
  label: string;
}

type FiltroAcquirenteEsterno = { id: number; label: string } | null;

interface TabOrdiniProps {
  commessaId: number;
  societaId: number;
  filtroAcquirente: FiltroAcquirenteEsterno;
  onResetFiltroAcquirente: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_LABEL: Record<StatoOrdine, string> = {
  BOZZA: 'Bozza',
  CONFERMATO: 'Confermato',
  IN_LAVORAZIONE: 'In lavorazione',
  CHIUSO: 'Chiuso',
};

const STATO_CLS: Record<StatoOrdine, string> = {
  BOZZA: 'bop-badge-neutral',
  CONFERMATO: 'bop-badge-info',
  IN_LAVORAZIONE: 'bop-badge-warning',
  CHIUSO: 'bop-badge-success',
};

const MODALITA_LABEL: Record<ModalitaEvasione, string> = {
  SAL: 'SAL',
  DDT: 'DDT',
  RAPPORTINO: 'Rapportino',
  MISTA: 'Mista',
};

const DESTINATARIO_LABEL: Record<DestinatarioOrdine, string> = {
  IMPRESA: 'Impresa',
  ACQUIRENTE: 'Acquirente',
};

const ChevronIcon = () => (
  <svg className="bop-tab-preventivi__acq-chevron" width="12" height="12" viewBox="0 0 24 24"
       fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * TabOrdini — vista filtrata della lista cross-commessa ristretta alla commessa.
 *
 * Specchio di TabPreventivi (§8 del documento di disegno Nodo 3). Consuma
 * GET /api/ordini con filtri pre-applicati (societaId + commessaId + acquirenteId
 * opzionale). Il click su una riga apre la SchedaOrdine in nuova tab.
 *
 * Niente CRUD qui: la creazione ordini avviene esclusivamente via conversione
 * da preventivo (Q4 del documento di disegno).
 */
export default function TabOrdini({
  commessaId,
  societaId,
  filtroAcquirente,
  onResetFiltroAcquirente,
}: TabOrdiniProps) {
  const [items, setItems] = useState<OrdineListaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Acquirenti per la combo locale
  const [acquirenteOpts, setAcquirenteOpts] = useState<AcquirenteOption[]>([]);
  const [filtroLocale, setFiltroLocale] = useState<AcquirenteOption | null>(null);
  const [comboOpen, setComboOpen] = useState(false);
  const [comboSearch, setComboSearch] = useState('');
  const comboRef = useRef<HTMLDivElement>(null);

  // Il filtro esterno (da TabCapitolato) ha precedenza sul locale
  const effectiveFilter = filtroAcquirente ?? filtroLocale;

  // Carica lista acquirenti per popolare la combo
  useEffect(() => {
    // @api GET /api/commesse/:id/acquirenti -> {"success":true,"data":[{"id":"number","nome":"string","cognome":"string","unitaCodice":"string"}]}
    apiFetch<{ success: boolean; data: { id: number; nome: string | null; cognome: string | null; unitaCodice: string }[] }>(
      `/api/commesse/${commessaId}/acquirenti`
    )
      .then(res => setAcquirenteOpts(
        (res.data || []).map(a => ({
          id: a.id,
          label: `${a.unitaCodice} — ${[a.nome, a.cognome].filter(Boolean).join(' ')}`,
        }))
      ))
      .catch(() => setAcquirenteOpts([]));
  }, [commessaId]);

  // Chiudi combo con click esterno
  useEffect(() => {
    if (!comboOpen) return;
    const handler = (e: MouseEvent) => {
      if (comboRef.current && !comboRef.current.contains(e.target as Node)) {
        setComboOpen(false);
        setComboSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [comboOpen]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const acqQ = effectiveFilter?.id ? `&acquirenteId=${effectiveFilter.id}` : '';
      // @api GET /api/ordini?societaId={id}&commessaId={commessaId}&acquirenteId={acqId} -> {"success":true,"data":[{"id":"number","numero":"string","societaId":"number","commessaId":"number","dataOrdine":"string","stato":"string","modalitaEvasione":"string|null","destinatarioOrdine":"string|null","acquirenteId":"number|null","totale":"string","preventivoOrigineId":"number|null","commessa":{...},"acquirente":{...}}]}
      const res = await apiFetch<{ success: boolean; data: OrdineListaItem[] }>(
        `/api/ordini?societaId=${societaId}&commessaId=${commessaId}${acqQ}`
      );
      setItems(res.data || []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [societaId, commessaId, effectiveFilter?.id]);

  useEffect(() => { load(); }, [load]);

  const selectAcquirente = (opt: AcquirenteOption | null) => {
    // Rimuovi il filtro esterno se presente
    if (filtroAcquirente) onResetFiltroAcquirente();
    setFiltroLocale(opt);
    setComboOpen(false);
    setComboSearch('');
  };

  const clearFilter = () => {
    if (filtroAcquirente) onResetFiltroAcquirente();
    setFiltroLocale(null);
  };

  const opzioniFiltrate = acquirenteOpts.filter(o =>
    comboSearch === '' || o.label.toLowerCase().includes(comboSearch.toLowerCase())
  );

  const apriOrdine = (id: number) => {
    window.open(`/commerciale/attivo/ordini/${id}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <div className="bop-text-muted bop-tab-preventivi__loading">Caricamento ordini…</div>;
  }
  if (error) {
    return <div className="bop-text-danger bop-tab-preventivi__error">{error}</div>;
  }

  return (
    <div className="bop-tab-preventivi">
      <div className="bop-page-header bop-tab-preventivi__header">
        <h2 className="bop-page-title bop-tab-preventivi__title">Ordini della commessa</h2>
      </div>

      <div className="bop-tab-preventivi__filter-bar">
        <div className="bop-tab-preventivi__acq-combo" ref={comboRef}>
          <span className="bop-tab-preventivi__acq-label">Acquirente</span>
          <button
            className={`bop-tab-preventivi__acq-btn${effectiveFilter ? ' bop-tab-preventivi__acq-btn--active' : ''}`}
            onClick={() => { setComboOpen(o => !o); setComboSearch(''); }}
            type="button"
          >
            <span className="bop-tab-preventivi__acq-btn-text">
              {effectiveFilter?.label ?? 'Tutti gli acquirenti'}
            </span>
            {effectiveFilter ? (
              <span
                className="bop-tab-preventivi__acq-clear"
                onClick={e => { e.stopPropagation(); clearFilter(); }}
                title="Rimuovi filtro"
              >
                ×
              </span>
            ) : (
              <ChevronIcon />
            )}
          </button>

          {comboOpen && (
            <div className="bop-tab-preventivi__acq-dropdown">
              <input
                className="bop-tab-preventivi__acq-search"
                placeholder="Cerca acquirente…"
                value={comboSearch}
                onChange={e => setComboSearch(e.target.value)}
                autoFocus
              />
              <ul className="bop-tab-preventivi__acq-list">
                <li
                  className="bop-tab-preventivi__acq-option bop-tab-preventivi__acq-option--all"
                  onClick={() => selectAcquirente(null)}
                >
                  Tutti gli acquirenti
                </li>
                {opzioniFiltrate.length === 0 ? (
                  <li className="bop-tab-preventivi__acq-empty">Nessun risultato</li>
                ) : (
                  opzioniFiltrate.map(o => (
                    <li
                      key={o.id}
                      className={`bop-tab-preventivi__acq-option${effectiveFilter?.id === o.id ? ' bop-tab-preventivi__acq-option--selected' : ''}`}
                      onClick={() => selectAcquirente(o)}
                    >
                      {o.label}
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bop-empty-state bop-tab-preventivi__empty">
          {effectiveFilter
            ? `Nessun ordine per ${effectiveFilter.label}.`
            : 'Nessun ordine cliente su questa commessa.'}
        </div>
      ) : (
        <div className="bop-card bop-tab-preventivi__card">
          <table className="bop-table bop-table--clickable bop-tab-preventivi__table">
            <thead>
              <tr>
                <th>Numero</th>
                <th>Data</th>
                <th>Destinatario</th>
                <th>Stato</th>
                <th>Modalità evasione</th>
                <th>Acquirente</th>
                <th className="bop-text-right">Totale</th>
              </tr>
            </thead>
            <tbody>
              {items.map(o => {
                const acquirenteLabel = o.acquirente
                  ? (
                    <span>
                      <strong>{o.acquirente.unitaCodice ?? '—'}</strong>
                      <br />
                      <span className="bop-text-muted">
                        {[o.acquirente.nome, o.acquirente.cognome].filter(Boolean).join(' ') || '—'}
                      </span>
                    </span>
                  )
                  : <span className="bop-text-muted">—</span>;
                return (
                  <tr key={o.id}
                      className="bop-tab-preventivi__row"
                      onClick={() => apriOrdine(o.id)}>
                    <td>{o.numero}</td>
                    <td>{new Date(o.dataOrdine).toLocaleDateString('it-IT')}</td>
                    <td>
                      {o.destinatarioOrdine
                        ? DESTINATARIO_LABEL[o.destinatarioOrdine]
                        : <span className="bop-text-muted">—</span>}
                    </td>
                    <td>
                      <span className={`bop-badge ${STATO_CLS[o.stato]}`}>
                        {STATO_LABEL[o.stato]}
                      </span>
                    </td>
                    <td>
                      {o.modalitaEvasione
                        ? MODALITA_LABEL[o.modalitaEvasione]
                        : <span className="bop-text-muted">—</span>}
                    </td>
                    <td>{acquirenteLabel}</td>
                    <td className="bop-text-right">
                      {o.totale != null
                        ? formatCurrency(Number(o.totale))
                        : <span className="bop-text-muted">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
